import { YarnLogo } from "./YarnLogo";
import { Fragment, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  bookFromSlugs,
  bookPagePath,
  seriesPath,
  SERIES,
} from "../config/site";
import { sectionFromFolder } from "../data/compendium-sections";
import { AppNavPhone, type Crumb } from "./AppNavPhone";
import { useAppNavPageName } from "./useAppNavPageName";
import { useHoldToOpen } from "./useHoldToOpen";

/** Books, then the series and book this address belongs to. */
function crumbsFor(pathname: string, pageName: string | null): Crumb[] {
  const [seriesSlug, bookSlug] = pathname.split("/").filter(Boolean);
  const crumbs: Crumb[] = [{ to: "/", label: "Home" }];
  if (seriesSlug === "legal") {
    return [...crumbs, { to: "/legal", label: "Legal" }];
  }
  const series = SERIES.find((item) => item.slug === seriesSlug);
  const book = bookFromSlugs(seriesSlug, bookSlug);
  if (series !== undefined) {
    crumbs.push({ to: seriesPath(series), label: series.title });
  }
  if (book !== undefined) {
    crumbs.push({
      to: bookPagePath(book),
      label: book.title,
      ...(book.shortTitle === undefined ? {} : { shortLabel: book.shortTitle }),
    });
  }
  const section = sectionFromFolder(bookSlug);
  if (series !== undefined && section !== undefined) {
    crumbs.push({ to: `${seriesPath(series)}#${section.folder}`, label: section.title(series.id) });
  }
  if (pageName !== null) {
    crumbs.push({ to: pathname, label: pageName });
  }
  return crumbs;
}

export function AppNav() {
  const { pathname } = useLocation();
  const crumbs = crumbsFor(pathname, useAppNavPageName(pathname));
  const linksRef = useRef<HTMLElement>(null);
  const holdLogo = useHoldToOpen("/status");

  // A long path scrolls on a phone. Show its end, which is where you are.
  useEffect(() => {
    const links = linksRef.current;
    if (links !== null) {
      links.scrollLeft = links.scrollWidth;
    }
  }, [crumbs.length, pathname]);

  return (
    <header className="app-nav">
      <Link className="app-nav__brand" to="/" draggable={false} {...holdLogo}><YarnLogo /></Link>
      <span className="app-nav__rule" aria-hidden="true" />
      <nav ref={linksRef} className="app-nav__links" aria-label="Main">
        {crumbs.map((crumb, index) => (
          <Fragment key={crumb.to}>
            {index === 0 ? null : <span className="app-nav__divider" aria-hidden="true">/</span>}
            <Link to={crumb.to}>{crumb.label}</Link>
          </Fragment>
        ))}
      </nav>
      <Link className="app-nav__legal" to="/legal">Legal</Link>
      <AppNavPhone crumbs={crumbs} />
    </header>
  );
}
